import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import type { ChangeHandler } from '../types';
import type { FreeBetsFromBackend } from 'app/api/services/freebets/types';
import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { useTranslation } from 'app/lang';
import { selectFreeBetsData } from 'app/lib/account/selectors';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';

import { type ChangeStakeTypeAmount, changeStakeTypeAmount } from '../../../actions/stakeTypes';
import useStakeTypesDisabledTitles from '../../../hooks/useStakeTypesDisabledTitles';
import { selectAllUsedFreeBets } from '../../../selectors/entries';
import BarContent from './BarContent';

import dictionary from '../FooterBar.dictionary.json';

// ---

type StakeTypeFooterBarProps = {
  maxValue?: number;
  betId?: number;
};

type StoreProps = {
  loggedIn: boolean;
  freebets: FreeBetsFromBackend;
  usedFreeBets: ReturnType<typeof selectAllUsedFreeBets>;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<ChangeStakeTypeAmount>;
};

const DefaultStakeTypeFooterBar: FC<StakeTypeFooterBarProps & StoreProps & DispatchProp> = ({
  freebets,
  usedFreeBets,
  loggedIn,
  maxValue,
  dispatch,
}) => {
  const { t } = useTranslation();
  const disabledTitles = useStakeTypesDisabledTitles();

  const freeBetItems = useMemo(() => {
    return freebets.map((freebet) => {
      const isAmountMoreThanMaxWinning = (maxValue && freebet.amount > maxValue);
      let disabledTitle = '';
      if (isAmountMoreThanMaxWinning) {
        disabledTitle = t(dictionary.notUseInThisMatch);
      }
      return ({
        ...freebet,
        disabledTitle: disabledTitles?.freeBets || disabledTitle,
        disabled: isAmountMoreThanMaxWinning || Boolean(disabledTitles?.freeBets),
      });
    });
  }, [usedFreeBets, freebets, maxValue, loggedIn]);

  const changeStakeTypeHandler: ChangeHandler = useCallback(({ value, account }) => {
    if (!account) {
      console.error('BetSlip -> StakeTypeFooterBar -> `account` not found');
      return;
    }

    dispatch(changeStakeTypeAmount(value, account));
  }, []);

  return (
    <BarContent freeBetItems={freeBetItems}
                onChange={changeStakeTypeHandler} />
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  return {
    loggedIn: selectIsLoggedIn(state),
    freebets: selectFreeBetsData(state) || [],
    usedFreeBets: selectAllUsedFreeBets(state),
  };
};

export default connect(mapStateToProps)(DefaultStakeTypeFooterBar);
